import {Component, computed, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {DatePipe} from '@angular/common';
import {Button} from 'primeng/button';
import {Card} from 'primeng/card';
import {Ticket, TicketCategory} from '../../models/ticket.model';
import {TicketsFacade} from '../../facades/tickets.facade';
import {NoCategoryHistoryContext} from '../../contexts/no-category-history.context';
import {NoCategoryHistoryComponent} from '../../components/no-category-history/no-category-history.component';
import {ShortcutContext} from '../../../../shared/shortcuts/shortcut.context';
import {DashboardContext} from '../../../dashboard/dashboard-context';
import {Drawer} from 'primeng/drawer';
import {Divider} from 'primeng/divider';

@Component({
	selector: 'app-no-category',
	imports: [
		Button,
		Card,
		DatePipe,
		NoCategoryHistoryComponent,
		Drawer,
		Divider
	],
	templateUrl: './ticket-no-category.html',
	host: { class: 'h-full flex-1' },
	styles: `
		.no-category-container {
			display: grid;
			grid-template-columns: minmax(20rem, 25rem) auto minmax(0, 1fr);
			gap: 1.25rem;
			height: 100%;
			min-height: 0;
			align-items: stretch;
		}
		.decision-panel,
		.mail-panel {
			min-height: 0;
		}
		.decision-panel {
			display: grid;
			grid-template-rows: auto 1fr;
			padding: 1rem;
		}
		.mail-panel {
			display: grid;
			grid-template-rows: auto 1fr;
			padding: 1.25rem;
		}
		.card-stack {
			position: relative;
			height: 23rem;
			min-height: 0;
			align-self: center;
			justify-self: center;
			width: min(100%, 20rem);
		}
		.ticket-info {
			touch-action: none;
			user-select: none;
			cursor: grab;
		}
		.ticket-summary {
			display: flex;
			flex-direction: column;
			gap: 1rem;
		}
		.summary-label {
			display: block;
			color: var(--p-text-muted-color);
			font-size: .75rem;
			margin-bottom: .35rem;
		}
		.summary-value {
			display: block;
			word-break: break-word;
			line-height: 1.4;
		}
		.mail-header {
			display: flex;
			justify-content: space-between;
			gap: 1rem;
			padding-bottom: 1rem;
			border-bottom: 1px solid var(--p-surface-200);
		}
		.mail-body {
			min-height: 0;
			overflow: auto;
			padding-top: 1rem;
			line-height: 1.55;
			overflow-wrap: anywhere;
		}
		.empty-state {
			height: 100%;
			display: flex;
			flex-direction: column;
			justify-content: center;
			align-items: center;
			gap: 1rem;
			color: var(--p-text-muted-color);
			text-align: center;
		}
		.empty-state i {
			font-size: 2rem;
		}
		.history-drawer-content {
			display: flex;
			flex-direction: column;
			gap: 1rem;
			height: 100%;
			min-height: 0;
		}
		:host ::ng-deep .category-divider {
			margin: 0;
		}
		:host ::ng-deep .no-category-history-drawer .p-drawer-content {
			min-height: 0;
		}
		:host ::ng-deep .p-card-body {
			height: 100%;
			display: grid;
			grid-template-rows: auto auto 1fr auto;
		}
		:host ::ng-deep .p-card-content {
			min-height: 0;
			overflow: hidden;
		}
		:host ::ng-deep .p-card-footer {
			align-self: end;
		}
		@media (max-width: 960px) {
			.no-category-container {
				grid-template-columns: 1fr;
			}
			:host ::ng-deep .category-divider {
				display: none;
			}
			.card-stack {
				height: 20rem;
			}
			.mail-panel {
				min-height: 20rem;
			}
		}
	`
})
export class TicketNoCategory implements OnInit, OnDestroy{
	private readonly historyCtx = inject(NoCategoryHistoryContext)
	private readonly dashboardCtx = inject(DashboardContext)
	private readonly ticketFacade = inject(TicketsFacade)
	private readonly shortcutsCtx = inject(ShortcutContext)

	readonly tickets = computed(() =>
		this.dashboardCtx.ticketToCategorize()
	)

	readonly currentTicket = computed(() => this.tickets()[0] ?? null)
	readonly historyDrawerVisible = signal(false)

	private startX = 0;
	private deltaX = 0;

	private activeEl: HTMLElement | null = null;
	private activeTicket: Ticket | null = null;
	private pointerId: number | null = null;

	ngOnInit() {
		this.shortcutsCtx.register({
			id: 'categorize-left',
			description: 'Classifica come Altro',
			keys: ['ARROWLEFT'],
			display: [
				{ kind: 'arrow', dir: 'left' }
			],
			action: () => this.commitByShortcut('left'),
			showInFooter: true,
			showInHelp: true,
			section: 'Modale',
			scope: 'dialog'
		})

		this.shortcutsCtx.register({
			id: 'categorize-right',
			description: 'Classifica come Supporto',
			keys: ['ARROWRIGHT'],
			display: [
				{ kind: 'arrow', dir: 'right' }
			],
			enabled: signal(this.tickets().length > 0),
			action: () => this.commitByShortcut('right'),
			showInFooter: true,
			showInHelp: true,
			section: 'Modale',
			scope: 'dialog'
		})

		this.shortcutsCtx.register({
			id: 'no-category-undo',
			description: 'Annulla ultima modifica',
			keys: ['MOD', 'Z'],
			display: [
				{ kind: 'modifier', value: 'MOD' },
				{ kind: 'key', value: 'Z' }
			],
			action: () => {
				if (!this.historyCtx.canUndo()) return;
				this.undoLast()
			},
			showInFooter: true,
			showInHelp: true,
			section: 'Modale',
			scope: 'dialog'
		});
	}

	ngOnDestroy() {
		this.clearShortcuts()
	}

	private clearShortcuts() {
		this.shortcutsCtx.unregister('categorize-left')
		this.shortcutsCtx.unregister('categorize-right')
		this.shortcutsCtx.unregister('no-category-undo')
	}

	onDown(e: PointerEvent, ticket: Ticket) {
		const path = e.composedPath() as HTMLElement[];
		const isButton = path.some(el => el.tagName === 'BUTTON' || el.tagName === 'P-BUTTON');

		if (isButton) return;

		this.activeEl = e.currentTarget as HTMLElement;
		this.activeTicket = ticket;

		this.startX = e.clientX;
		this.deltaX = 0;

		this.pointerId = e.pointerId;

		this.activeEl.setPointerCapture(e.pointerId);
	}

	onMove(e: PointerEvent) {
		if (!this.activeEl || this.pointerId !== e.pointerId) return;

		this.deltaX = e.clientX - this.startX;
		const rot = this.deltaX * 0.05;

		this.activeEl.style.transform =
			`translateX(${this.deltaX}px) rotate(${rot}deg)`;
	}

	onUp() {
		if (!this.activeEl || !this.activeTicket) return;

		if (Math.abs(this.deltaX) < 30) {
			this.resetDragState();
			return;
		}

		const dir: 'left' | 'right' =
			this.deltaX > 0 ? 'right' : 'left';

		this.animateOut(dir);
	}

	animateOut(dir: 'left' | 'right') {
		if (!this.activeEl || !this.activeTicket) return;

		const x = dir === 'right' ? 1200 : -1200;
		const rot = dir === 'right' ? 20 : -20;

		const startTransform =
			this.activeEl.style.transform || 'translateX(0) rotate(0)';

		this.activeEl.animate(
			[
				{ transform: startTransform },
				{ transform: `translateX(${x}px) rotate(${rot}deg)` }
			],
			{
				duration: 320,
				easing: 'cubic-bezier(.22,.61,.36,1)',
				fill: 'forwards'
			}
		).onfinish = () => {
			this.commitCategory(dir);
			this.resetDragState();
		};
	}

	private resetDragState() {
		if (this.activeEl && this.pointerId !== null) {
			this.activeEl.releasePointerCapture(this.pointerId);
		}

		this.startX = 0;
		this.deltaX = 0;
		this.pointerId = null;
		this.activeEl = null;
		this.activeTicket = null;
	}

	private commitCategory(dir: 'left' | 'right') {
		if (!this.activeTicket) return;

		const categoryMap: Record<'left' | 'right', TicketCategory> = {
			left: 'OTHER',
			right: 'SUPPORT'
		};

		const category = categoryMap[dir];

		this.historyCtx.add({
			ticketId: this.activeTicket.id,
			subject: this.activeTicket.subject,
			from: this.activeTicket.category,
			to: category,
			timestamp: Date.now()
		})

		this.ticketFacade.updateCategory(
			this.activeTicket.id,
			category
		);
	}

	stackTransform(i: number) {
		if (i === 0) return '';
		return `scale(${1 - i * 0.04}) translateY(${i * 10}px)`;
	}

	startCommit(
		ticket: Ticket,
		el: HTMLElement,
		dir: 'left' | 'right'
	) {
		this.activeTicket = ticket;
		this.activeEl = el;
		this.pointerId = null;
		this.deltaX = dir === 'right' ? 1 : -1;

		this.animateOut(dir);
	}

	commitByShortcut(dir: 'left' | 'right') {
		const ticket = this.tickets()[0];
		if (!ticket) return;

		const el = document.querySelector(
			`[data-ticket-id="${ticket.id}"]`
		) as HTMLElement | null;

		if (!el) return;

		this.startCommit(ticket, el, dir);
	}

	private undoLast() {
		const last = this.historyCtx.undoLast();
		if (!last) return;

		this.ticketFacade.updateCategory(last.ticketId, last.from);
	}
}
